import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { staffAPI, leaveAPI, clubsAPI } from "../lib/api";
import toast from "react-hot-toast";
import { storageService } from "../lib/storage";
import RosterManagement from "./RosterManagement";
import LeaveManagement from "./LeaveManagement";

const STAFF_ROLES = [
  { value: "Super Admin", label: "Super Admin" },
  { value: "Admin", label: "Admin" },
  { value: "Manager", label: "Manager" },
  { value: "HR", label: "HR" },
  { value: "GRE", label: "GRE (Guest Relations Executive)" },
  { value: "Cashier", label: "Cashier" },
  { value: "Affiliate", label: "Affiliate" },
  { value: "Dealer", label: "Dealer" },
  { value: "FNB", label: "FNB" },
  { value: "Staff", label: "Staff (Custom Role)" },
];

const DOC_IMAGE_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const DOC_PDF_MIME_TYPES = ["application/pdf"];
const DOC_ALLOWED_MIME_TYPES = [...DOC_IMAGE_MIME_TYPES, ...DOC_PDF_MIME_TYPES];

function getAadhaarDocMeta(value) {
  if (!value || typeof value !== "string") {
    return { mode: "single", singleUrl: "" };
  }
  try {
    const parsed = JSON.parse(value);
    if (
      parsed &&
      parsed.type === "aadhaar_dual_image" &&
      typeof parsed.frontUrl === "string" &&
      typeof parsed.backUrl === "string"
    ) {
      return {
        mode: "dual-image",
        frontUrl: parsed.frontUrl,
        backUrl: parsed.backUrl,
      };
    }
  } catch (_err) {
    // Not JSON, treat as legacy/single URL.
  }
  return { mode: "single", singleUrl: value };
}

export default function StaffManagement({ selectedClubId }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("staff"); // 'staff', 'roster', or 'leave'

  // Check current user role
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isHR = user.role === 'HR';
  const isAdmin = user.role === 'Admin';

  const AVAILABLE_ROLES = (() => {
    if (isHR) {
      // HR cannot create Admin or Super Admin
      return STAFF_ROLES.filter(r => r.value !== "Super Admin" && r.value !== "Admin");
    }
    if (isAdmin) {
      // Admin cannot create Super Admin
      return STAFF_ROLES.filter(r => r.value !== "Super Admin");
    }
    return STAFF_ROLES;
  })();
  // Fetch club data to check if both poker and rummy are enabled
  const { data: clubData } = useQuery({
    queryKey: ['club-data', selectedClubId],
    queryFn: () => clubsAPI.getClub(selectedClubId),
    enabled: !!selectedClubId,
  });
  const hasBothGames = clubData?.pokerEnabled && clubData?.rummyEnabled;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    role: "",
    status: "",
    search: "",
    sortBy: "createdAt",
    sortOrder: "DESC",
  });

  // Form state
  const [staffForm, setStaffForm] = useState({
    name: "",
    role: isHR ? "Staff" : isAdmin ? "Admin" : "Super Admin",
    email: "",
    phone: "",
    employeeId: "",
    customRoleName: "",
    gameType: "",
    aadharDocumentUrl: "",
    panDocumentUrl: "",
    baseSalary: "",
    salaryType: "Monthly",
  });

  const [suspendForm, setSuspendForm] = useState({ reason: "" });
  const [uploadingAadhar, setUploadingAadhar] = useState(false);
  const [uploadingPan, setUploadingPan] = useState(false);
  const [aadharUploadMode, setAadharUploadMode] = useState("");
  const [aadharFrontDocumentUrl, setAadharFrontDocumentUrl] = useState("");
  const [aadharBackDocumentUrl, setAadharBackDocumentUrl] = useState("");

  // Fetch staff
  const { data: staffData, isLoading: staffLoading } = useQuery({
    queryKey: ["staff-management", selectedClubId, filters],
    queryFn: () => staffAPI.getAllStaffMembers(selectedClubId, filters),
    enabled: !!selectedClubId,
  });

  // Fetch pending leave applications for badge count
  const { data: pendingLeaves = [] } = useQuery({
    queryKey: ['pendingLeaveApplications', selectedClubId],
    queryFn: () => leaveAPI.getPendingLeaveApplications(selectedClubId),
    enabled: !!selectedClubId,
  });

  const pendingLeaveCount = pendingLeaves?.length || 0;

  // Create staff mutation
  const createMutation = useMutation({
    mutationFn: (data) => staffAPI.createStaffMember(selectedClubId, data),
    onSuccess: (response) => {
      toast.success("Staff member created successfully!");
      if (response.staff.tempPasswordPlainText) {
        setTempPassword({
          password: response.staff.tempPasswordPlainText,
          name: response.staff.name,
          email: response.staff.email,
          role: response.staff.role,
          affiliateCode: response.staff.affiliateCode,
          isReset: false,
        });
        setShowPasswordModal(true);
      }
      queryClient.invalidateQueries(["staff-management", selectedClubId]);
      setShowCreateModal(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create staff member");
    },
  });

  // Update staff mutation
  const updateMutation = useMutation({
    mutationFn: ({ staffId, data }) => staffAPI.updateStaffMember(selectedClubId, staffId, data),
    onSuccess: () => {
      toast.success("Staff member updated successfully!");
      queryClient.invalidateQueries(["staff-management", selectedClubId]);
      setShowEditModal(false);
      setSelectedStaff(null);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update staff member");
    },
  });

  // Suspend staff mutation
  const suspendMutation = useMutation({
    mutationFn: ({ staffId, reason }) => staffAPI.suspendStaffMember(selectedClubId, staffId, reason),
    onSuccess: () => {
      toast.success("Staff member suspended successfully!");
      queryClient.invalidateQueries(["staff-management", selectedClubId]);
      setShowSuspendModal(false);
      setSelectedStaff(null);
      setSuspendForm({ reason: "" });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to suspend staff member");
    },
  });

  // Reactivate staff mutation
  const reactivateMutation = useMutation({
    mutationFn: (staffId) => staffAPI.reactivateStaffMember(selectedClubId, staffId),
    onSuccess: () => {
      toast.success("Staff member reactivated successfully!");
      queryClient.invalidateQueries(["staff-management", selectedClubId]);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to reactivate staff member");
    },
  });

  // Delete staff mutation
  const deleteMutation = useMutation({
    mutationFn: (staffId) => staffAPI.deleteStaffMember(selectedClubId, staffId),
    onSuccess: () => {
      toast.success("Staff member deleted successfully!");
      queryClient.invalidateQueries(["staff-management", selectedClubId]);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete staff member");
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async (staffId) => {
      const response = await staffAPI.resetStaffPassword(selectedClubId, staffId);
      // Get staff details for the modal
      const staff = staffData?.staff.find((s) => s.id === staffId);
      return { ...response, staff };
    },
    onSuccess: (response) => {
      toast.success("Password reset successfully!");
      setTempPassword({
        password: response.tempPassword,
        name: response.staff?.name,
        email: response.staff?.email,
        role: response.staff?.role,
        affiliateCode: response.staff?.affiliateCode,
        isReset: true,
      });
      setShowPasswordModal(true);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to reset password");
    },
  });

  const handleFileUpload = async (file, type) => {
    if (!DOC_ALLOWED_MIME_TYPES.includes(file?.type || "")) {
      toast.error("Only JPG, PNG, WEBP, or PDF files are allowed");
      return;
    }
    if ((type === "aadhar_front" || type === "aadhar_back") && !DOC_IMAGE_MIME_TYPES.includes(file.type)) {
      toast.error("Aadhaar Front/Back must be image files only");
      return;
    }
    if (type === "aadhar_pdf" && !DOC_PDF_MIME_TYPES.includes(file.type)) {
      toast.error("Aadhaar PDF upload requires a PDF file");
      return;
    }

    if (type.startsWith("aadhar")) setUploadingAadhar(true);
    else setUploadingPan(true);

    try {
      const url = await storageService.uploadDocument(file, selectedClubId, "staff-kyc");
      if (type === "aadhar_pdf") {
        setStaffForm((prev) => ({ ...prev, aadharDocumentUrl: url }));
        setAadharFrontDocumentUrl("");
        setAadharBackDocumentUrl("");
        toast.success("Aadhaar PDF uploaded successfully");
      } else if (type === "aadhar_front") {
        setAadharFrontDocumentUrl(url);
        toast.success("Aadhaar Front uploaded successfully");
      } else if (type === "aadhar_back") {
        setAadharBackDocumentUrl(url);
        toast.success("Aadhaar Back uploaded successfully");
      } else {
        setStaffForm((prev) => ({ ...prev, panDocumentUrl: url }));
        toast.success("PAN card uploaded successfully");
      }
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      if (type.startsWith("aadhar")) setUploadingAadhar(false);
      else setUploadingPan(false);
    }
  };

  const resetForm = () => {
    setStaffForm({
      name: "",
      role: isHR ? "Staff" : isAdmin ? "Admin" : "Super Admin",
      email: "",
      phone: "",
      employeeId: "",
      customRoleName: "",
      gameType: "",
      aadharDocumentUrl: "",
      panDocumentUrl: "",
      baseSalary: "",
      salaryType: "Monthly",
    });
    setAadharUploadMode("");
    setAadharFrontDocumentUrl("");
    setAadharBackDocumentUrl("");
  };

  const handleCreateStaff = () => {
    if (!staffForm.name || !staffForm.email || !staffForm.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (staffForm.role === "Staff" && !staffForm.customRoleName) {
      toast.error("Please enter a custom role name");
      return;
    }

    if (hasBothGames && (staffForm.role === "Dealer" || staffForm.role === "Manager") && !staffForm.gameType) {
      toast.error("Please select a game type for this " + staffForm.role.toLowerCase());
      return;
    }

    if (!aadharUploadMode) {
      toast.error("Please select Aadhaar upload type (Image or PDF)");
      return;
    }

    // Validate Aadhaar docs based on upload mode.
    if (aadharUploadMode === "image") {
      if (!aadharFrontDocumentUrl || !aadharBackDocumentUrl) {
        toast.error("Please upload Aadhaar Front and Aadhaar Back images");
        return;
      }
    } else if (!staffForm.aadharDocumentUrl) {
      toast.error("Please upload Aadhaar PDF document");
      return;
    }

    if (!staffForm.panDocumentUrl) {
      toast.error("PAN document is required to create a staff member");
      return;
    }

    const payload = {
      ...staffForm,
      aadharDocumentUrl:
        aadharUploadMode === "image"
          ? JSON.stringify({
              type: "aadhaar_dual_image",
              frontUrl: aadharFrontDocumentUrl,
              backUrl: aadharBackDocumentUrl,
            })
          : staffForm.aadharDocumentUrl,
    };
    if (payload.baseSalary) {
      payload.baseSalary = Number(payload.baseSalary);
    } else {
      delete payload.baseSalary;
    }
    createMutation.mutate(payload);
  };

  const handleEditStaff = (staff) => {
    setSelectedStaff(staff);
    setStaffForm({
      name: staff.name,
      role: staff.role,
      email: staff.email,
      phone: staff.phone,
      employeeId: staff.employeeId || "",
      customRoleName: staff.customRoleName || "",
      gameType: staff.gameType || "",
      aadharDocumentUrl: staff.aadharDocumentUrl || "",
      panDocumentUrl: staff.panDocumentUrl || "",
      baseSalary: staff.baseSalary || "",
      salaryType: staff.salaryType || "Monthly",
    });
    setShowEditModal(true);
  };

  const handleUpdateStaff = () => {
    if (!staffForm.name || !staffForm.email || !staffForm.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate KYC documents - both must be present
    if (!staffForm.aadharDocumentUrl) {
      toast.error("Aadhar document is required. Cannot remove Aadhar document.");
      return;
    }
    if (!staffForm.panDocumentUrl) {
      toast.error("PAN document is required. Cannot remove PAN document.");
      return;
    }

    updateMutation.mutate({ staffId: selectedStaff.id, data: staffForm });
  };

  const handleSuspend = (staff) => {
    setSelectedStaff(staff);
    setShowSuspendModal(true);
  };

  const handleSuspendConfirm = () => {
    if (!suspendForm.reason || suspendForm.reason.length < 5) {
      toast.error("Please provide a reason (minimum 5 characters)");
      return;
    }

    suspendMutation.mutate({ staffId: selectedStaff.id, reason: suspendForm.reason });
  };

  const handleReactivate = (staff) => {
    if (window.confirm(`Reactivate ${staff.name}?`)) {
      reactivateMutation.mutate(staff.id);
    }
  };

  const handleDelete = (staff) => {
    if (window.confirm(`Are you sure you want to delete ${staff.name}? This action cannot be undone.`)) {
      deleteMutation.mutate(staff.id);
    }
  };

  const handleResetPassword = (staff) => {
    if (window.confirm(`Reset password for ${staff.name}?`)) {
      resetPasswordMutation.mutate(staff.id);
    }
  };

  const handleViewStaff = (staff) => {
    setSelectedStaff(staff);
    setShowViewModal(true);
  };

  const staff = staffData?.staff || [];
  const selectedStaffAadhaarMeta = selectedStaff
    ? getAadhaarDocMeta(selectedStaff.aadharDocumentUrl)
    : { mode: "single", singleUrl: "" };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "bg-green-500/20 text-green-300 border-green-500";
      case "Suspended":
        return "bg-red-500/20 text-red-300 border-red-500";
      case "On Break":
        return "bg-yellow-500/20 text-yellow-300 border-yellow-500";
      default:
        return "bg-gray-500/20 text-gray-300 border-gray-500";
        }
    };

    return (
        <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white">Staff Management</h1>
        {activeTab === "staff" && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-400 hover:to-pink-500 text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-all"
          >
            ➕ Create Staff
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700">
        <button
          onClick={() => setActiveTab("staff")}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === "staff"
              ? "text-white border-b-2 border-purple-500"
              : "text-gray-400 hover:text-white"
          }`}
        >
          Staff Members
        </button>
        <button
          onClick={() => setActiveTab("roster")}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === "roster"
              ? "text-white border-b-2 border-purple-500"
              : "text-gray-400 hover:text-white"
          }`}
        >
          Roster Management
        </button>
        <button
          onClick={() => setActiveTab("leave")}
          className={`px-6 py-3 font-semibold transition-colors relative ${
            activeTab === "leave"
              ? "text-white border-b-2 border-purple-500"
              : "text-gray-400 hover:text-white"
          }`}
        >
          <span className="flex items-center gap-2">
            Leave Management
            {pendingLeaveCount > 0 && (
              <span className="bg-amber-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                {pendingLeaveCount > 9 ? "9+" : pendingLeaveCount}
              </span>
            )}
          </span>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "roster" && (
        <RosterManagement selectedClubId={selectedClubId} />
      )}

      {activeTab === "leave" && selectedClubId && (
        <LeaveManagement clubId={selectedClubId} />
      )}

      {activeTab === "staff" && (
        <>

      {/* Filters */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-white text-sm mb-1 block">Role</label>
            <select
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value })}
            >
              <option value="">All Roles</option>
              {STAFF_ROLES.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-white text-sm mb-1 block">Status</label>
            <select
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Suspended">Suspended</option>
              <option value="On Break">On Break</option>
            </select>
          </div>

          <div>
            <label className="text-white text-sm mb-1 block">Sort By</label>
            <select
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
              value={filters.sortBy}
              onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
            >
              <option value="createdAt">Date Created</option>
              <option value="name">Name</option>
              <option value="role">Role</option>
            </select>
          </div>

          <div>
            <label className="text-white text-sm mb-1 block">Search</label>
            <input
              type="text"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
              placeholder="Name, email, phone..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Staff List */}
      {staffLoading ? (
        <div className="text-white text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p>Loading staff...</p>
        </div>
      ) : staff.length === 0 ? (
        <div className="bg-slate-800 rounded-xl p-12 text-center border border-slate-700">
          <div className="text-6xl mb-4">👥</div>
          <h3 className="text-xl font-semibold text-white mb-2">No Staff Members Yet</h3>
          <p className="text-gray-400">Create your first staff member to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {staff.map((member) => (
            <div
              key={member.id}
              className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-purple-600 transition-all"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-3">
                    <h3 className="text-xl font-bold text-white">{member.name}</h3>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(
                        member.status
                      )}`}
                    >
                      {member.status}
                    </span>
                    <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-purple-500/20 text-purple-300 border border-purple-500">
                      {member.role === "Staff" && member.customRoleName
                        ? member.customRoleName
                        : member.role}
                    </span>
                    {member.gameType === 'poker' ? (
                      <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-indigo-500/20 text-indigo-300 border border-indigo-500">
                        ♠ Poker
                      </span>
                    ) : member.gameType === 'rummy' ? (
                      <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-amber-500/20 text-amber-300 border border-amber-500">
                        🃏 Rummy
                      </span>
                    ) : (
                      <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-slate-500/20 text-slate-300 border border-slate-500">
                        🎯 General
                      </span>
                    )}
                    {member.affiliateCode && (
                      <span className="inline-block px-3 py-1 rounded-full text-sm font-mono font-semibold bg-yellow-500/20 text-yellow-300 border border-yellow-500">
                        {member.affiliateCode}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-400">Email</p>
                      <p className="text-white font-semibold">{member.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Phone</p>
                      <p className="text-white font-semibold">{member.phone}</p>
                    </div>
                    {member.employeeId && (
                      <div>
                        <p className="text-sm text-gray-400">Employee ID</p>
                        <p className="text-white font-mono font-semibold">{member.employeeId}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-400">Created</p>
                      <p className="text-white font-semibold">
                        {new Date(member.createdAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                      </p>
                    </div>
                    {Number(member.baseSalary || 0) > 0 && (
                      <div>
                        <p className="text-sm text-gray-400">Salary</p>
                        <p className="text-green-400 font-semibold">
                          ₹{Number(member.baseSalary).toFixed(0)} / {member.salaryType || "Monthly"}
                        </p>
                      </div>
                    )}
                  </div>

                  {member.status === "Suspended" && member.suspendedReason && (
                    <div className="mt-3 bg-red-900/30 border border-red-500/50 rounded-lg p-3">
                      <p className="text-sm text-red-300">
                        <strong>Suspension Reason:</strong> {member.suspendedReason}
                      </p>
                    </div>
                  )}
            </div>

                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleViewStaff(member)}
                    className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                  >
                    View
                  </button>

                  {!isHR && (
                    <>
                      <button
                        onClick={() => handleEditStaff(member)}
                        className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                      >
                        Edit
                      </button>

                      {member.status === "Active" && (
                        <button
                          onClick={() => handleSuspend(member)}
                          className="bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                        >
                          Suspend
                        </button>
                      )}

                      {member.status === "Suspended" && (
                        <button
                          onClick={() => handleReactivate(member)}
                          className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                        >
                          Reactivate
                        </button>
                      )}

                      <button
                        onClick={() => handleResetPassword(member)}
                        className="bg-yellow-600 hover:bg-yellow-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                      >
                        Reset Password
                      </button>

                      <button
                        onClick={() => handleDelete(member)}
                        className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Staff Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full border border-purple-600 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Create New Staff Member</h2>

                                <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                                        <div>
                  <label className="text-white text-sm mb-1 block">Full Name *</label>
                                            <input
                                                type="text"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                    placeholder="John Doe"
                    value={staffForm.name}
                    onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                                            />
                                        </div>

                <div>
                  <label className="text-white text-sm mb-1 block">Role *</label>
                  <select
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                    value={staffForm.role}
                    onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })}
                  >
              {AVAILABLE_ROLES.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {staffForm.role === "Staff" && (
                                        <div>
                  <label className="text-white text-sm mb-1 block">Custom Role Name *</label>
                                            <input
                                                type="text"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., Security, Maintenance, etc."
                    value={staffForm.customRoleName}
                    onChange={(e) => setStaffForm({ ...staffForm, customRoleName: e.target.value })}
                                            />
                                        </div>
              )}

              {(staffForm.role === "Dealer" || staffForm.role === "Manager") && (
                <div>
                  <label className="text-white text-sm mb-1 block">Game Type {hasBothGames ? '*' : '(Optional)'}</label>
                  <select
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                    value={staffForm.gameType}
                    onChange={(e) => setStaffForm({ ...staffForm, gameType: e.target.value })}
                  >
                    <option value="">General (No specific game)</option>
                    <option value="poker">♠ Poker</option>
                    <option value="rummy">🃏 Rummy</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    {staffForm.role === "Dealer"
                      ? "Dealer will only be assigned to tables of this game type"
                      : "Manager will manage tables of this game type"}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                                    <div>
                  <label className="text-white text-sm mb-1 block">Email *</label>
                                        <input
                                            type="email"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                    placeholder="john@example.com"
                    value={staffForm.email}
                    onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                                        />
                                    </div>

                                    <div>
                  <label className="text-white text-sm mb-1 block">Phone *</label>
                                        <input
                                            type="tel"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                    placeholder="+91 1234567890"
                    value={staffForm.phone}
                    onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                                        />
                                    </div>
                                    </div>

                                        <div>
                <label className="text-white text-sm mb-1 block">Employee ID (Optional)</label>
                                            <input
                                                type="text"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                  placeholder="EMP001"
                  value={staffForm.employeeId}
                  onChange={(e) => setStaffForm({ ...staffForm, employeeId: e.target.value })}
                />
                                        </div>

              {/* Salary Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white text-sm mb-1 block">Base Salary (Optional)</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                    placeholder="0.00"
                    value={staffForm.baseSalary}
                    onChange={(e) => setStaffForm({ ...staffForm, baseSalary: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-white text-sm mb-1 block">Salary Type</label>
                  <select
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                    value={staffForm.salaryType}
                    onChange={(e) => setStaffForm({ ...staffForm, salaryType: e.target.value })}
                  >
                    <option value="Monthly">Monthly</option>
                    <option value="Weekly">Weekly</option>
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-white text-sm mb-1 block">Aadhaar Upload Type *</label>
                  <select
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                    value={aadharUploadMode}
                    onChange={(e) => {
                      const mode = e.target.value;
                      setAadharUploadMode(mode);
                      setAadharFrontDocumentUrl("");
                      setAadharBackDocumentUrl("");
                      setStaffForm((prev) => ({ ...prev, aadharDocumentUrl: "" }));
                    }}
                  >
                    <option value="">Select Aadhaar upload type</option>
                    <option value="image">Image (Front + Back)</option>
                    <option value="pdf">Single PDF (Front & Back together)</option>
                  </select>
                </div>

                {aadharUploadMode === "image" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-white text-sm mb-1 block">Aadhaar Front *</label>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                        onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], "aadhar_front")}
                        disabled={uploadingAadhar}
                      />
                      {aadharFrontDocumentUrl && <p className="text-sm text-green-400 mt-1">✓ Uploaded</p>}
                    </div>
                    <div>
                      <label className="text-white text-sm mb-1 block">Aadhaar Back *</label>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                        onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], "aadhar_back")}
                        disabled={uploadingAadhar}
                      />
                      {aadharBackDocumentUrl && <p className="text-sm text-green-400 mt-1">✓ Uploaded</p>}
                    </div>
                  </div>
                )}

                {aadharUploadMode === "pdf" && (
                  <div>
                    <label className="text-white text-sm mb-1 block">Aadhaar PDF *</label>
                    <input
                      type="file"
                      accept="application/pdf"
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                      onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], "aadhar_pdf")}
                      disabled={uploadingAadhar}
                    />
                    {staffForm.aadharDocumentUrl && <p className="text-sm text-green-400 mt-1">✓ Uploaded</p>}
                  </div>
                )}

                {uploadingAadhar && <p className="text-sm text-gray-400 mt-1">Uploading Aadhaar...</p>}

                <div>
                  <label className="text-white text-sm mb-1 block">PAN Card *</label>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                    onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], "pan")}
                    disabled={uploadingPan}
                  />
                  {uploadingPan && <p className="text-sm text-gray-400 mt-1">Uploading PAN...</p>}
                  {staffForm.panDocumentUrl && <p className="text-sm text-green-400 mt-1">✓ Uploaded</p>}
                </div>
              </div>
                            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCreateStaff}
                disabled={createMutation.isLoading}
                className="flex-1 bg-purple-600 hover:bg-purple-500 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
              >
                {createMutation.isLoading ? "Creating..." : "Create Staff Member"}
              </button>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-colors"
              >
                Cancel
              </button>
                                </div>
                            </div>
                        </div>
      )}

      {/* Edit Staff Modal - Similar structure to Create */}
      {showEditModal && selectedStaff && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full border border-blue-600 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Edit Staff Member</h2>

                            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                                <div>
                  <label className="text-white text-sm mb-1 block">Full Name *</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    value={staffForm.name}
                    onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                  />
                                </div>

                                <div>
                  <label className="text-white text-sm mb-1 block">Email *</label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    value={staffForm.email}
                    onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })}
                  />
                            </div>
                        </div>

              <div className="grid grid-cols-2 gap-4">
                                <div>
                  <label className="text-white text-sm mb-1 block">Phone *</label>
                  <input
                    type="tel"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    value={staffForm.phone}
                    onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                  />
                                </div>

                                <div>
                  <label className="text-white text-sm mb-1 block">Employee ID</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    value={staffForm.employeeId}
                    onChange={(e) => setStaffForm({ ...staffForm, employeeId: e.target.value })}
                  />
                                </div>
                                </div>

              {staffForm.role === "Staff" && (
                                <div>
                  <label className="text-white text-sm mb-1 block">Custom Role Name *</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    value={staffForm.customRoleName}
                    onChange={(e) => setStaffForm({ ...staffForm, customRoleName: e.target.value })}
                  />
                                </div>
              )}

              {(staffForm.role === "Dealer" || staffForm.role === "Manager") && (
                <div>
                  <label className="text-white text-sm mb-1 block">Game Type</label>
                  <select
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    value={staffForm.gameType}
                    onChange={(e) => setStaffForm({ ...staffForm, gameType: e.target.value })}
                  >
                    <option value="">General (No specific game)</option>
                    <option value="poker">♠ Poker</option>
                    <option value="rummy">🃏 Rummy</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    Assign a specific game to this {staffForm.role.toLowerCase()}
                  </p>
                </div>
              )}

              {/* Salary Fields in Edit */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white text-sm mb-1 block">Base Salary</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                    value={staffForm.baseSalary}
                    onChange={(e) => setStaffForm({ ...staffForm, baseSalary: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-white text-sm mb-1 block">Salary Type</label>
                  <select
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    value={staffForm.salaryType}
                    onChange={(e) => setStaffForm({ ...staffForm, salaryType: e.target.value })}
                  >
                    <option value="Monthly">Monthly</option>
                    <option value="Weekly">Weekly</option>
                  </select>
                </div>
              </div>
                                </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleUpdateStaff}
                disabled={updateMutation.isLoading}
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
              >
                {updateMutation.isLoading ? "Updating..." : "Update Staff Member"}
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedStaff(null);
                  resetForm();
                }}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-colors"
              >
                Cancel
                                </button>
                            </div>
                        </div>
                    </div>
      )}

      {/* Suspend Modal */}
      {showSuspendModal && selectedStaff && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full border border-orange-600 max-h-[85vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-white mb-4">Suspend Staff Member</h2>
            <p className="text-gray-300 mb-4">
              You are about to suspend <strong>{selectedStaff.name}</strong>. Please provide a reason:
            </p>

            <textarea
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 h-32"
              placeholder="Enter suspension reason (minimum 5 characters)"
              value={suspendForm.reason}
              onChange={(e) => setSuspendForm({ reason: e.target.value })}
            />

            <div className="flex gap-3 mt-6">
                                    <button
                onClick={handleSuspendConfirm}
                disabled={suspendMutation.isLoading}
                className="flex-1 bg-orange-600 hover:bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
                                    >
                {suspendMutation.isLoading ? "Suspending..." : "Suspend"}
                                    </button>
                                    <button
                onClick={() => {
                  setShowSuspendModal(false);
                  setSelectedStaff(null);
                  setSuspendForm({ reason: "" });
                }}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-colors"
              >
                Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
      )}

      {/* Temporary Password Modal */}
      {showPasswordModal && tempPassword && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full border border-green-600 max-h-[85vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-white mb-4">
              {tempPassword.isReset ? "Password Reset Successfully!" : "Staff Member Created Successfully!"}
            </h2>
            
            <div className="bg-green-900/30 border border-green-500/50 rounded-lg p-4 mb-4">
              <p className="text-white mb-2">
                <strong>Name:</strong> {tempPassword.name}
              </p>
              <p className="text-white mb-2">
                <strong>Role:</strong> {tempPassword.role}
              </p>
              <p className="text-white mb-2">
                <strong>Email:</strong> {tempPassword.email}
              </p>
              {tempPassword.affiliateCode && (
                <p className="text-yellow-300 mb-2 font-mono">
                  <strong>Affiliate Code:</strong> {tempPassword.affiliateCode}
                </p>
              )}
              <p className="text-white mb-2">
                <strong>Temporary Password:</strong>
              </p>
              <div className="bg-slate-700 rounded p-3 font-mono text-lg text-center text-green-300 select-all">
                {tempPassword.password}
              </div>
            </div>

            <p className="text-sm text-gray-400 mb-4">
              ⚠️ Please save this password and share it with the staff member.{" "}
              {tempPassword.isReset
                ? "They will be required to reset it on next login."
                : "They will be required to reset it on first login. This password will not be shown again."}
            </p>

                            <button
              onClick={() => {
                setShowPasswordModal(false);
                setTempPassword(null);
              }}
              className="w-full bg-green-600 hover:bg-green-500 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              I've Saved the Password
                            </button>
                        </div>
                    </div>
      )}

      {/* View Staff Details Modal */}
      {showViewModal && selectedStaff && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-slate-800 rounded-xl p-6 max-w-4xl w-full border border-purple-600 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Staff Member Details</h2>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedStaff(null);
                }}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-slate-700/50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4 border-b border-slate-600 pb-2">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Full Name</p>
                    <p className="text-white font-semibold text-lg">{selectedStaff.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Role</p>
                    <p className="text-white font-semibold">
                      {selectedStaff.role === "Staff" && selectedStaff.customRoleName
                        ? selectedStaff.customRoleName
                        : selectedStaff.role}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Email</p>
                    <p className="text-white">{selectedStaff.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Phone</p>
                    <p className="text-white">{selectedStaff.phone}</p>
                  </div>
                  {selectedStaff.employeeId && (
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Employee ID</p>
                      <p className="text-white font-mono">{selectedStaff.employeeId}</p>
                    </div>
                  )}
                  {selectedStaff.affiliateCode && (
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Affiliate Code</p>
                      <p className="text-yellow-300 font-mono font-bold text-lg">
                        {selectedStaff.affiliateCode}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Status</p>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(
                        selectedStaff.status
                      )}`}
                    >
                      {selectedStaff.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Game Type</p>
                    <p>
                      {selectedStaff.gameType === 'poker' ? (
                        <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-indigo-500/20 text-indigo-300 border border-indigo-500">
                          ♠ Poker
                        </span>
                      ) : selectedStaff.gameType === 'rummy' ? (
                        <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-amber-500/20 text-amber-300 border border-amber-500">
                          🃏 Rummy
                        </span>
                      ) : (
                        <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold bg-slate-500/20 text-slate-300 border border-slate-500">
                          🎯 General
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Created At</p>
                    <p className="text-white">
                      {new Date(selectedStaff.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Base Salary</p>
                    <p className="text-green-400 font-semibold">
                      {Number(selectedStaff.baseSalary || 0) > 0
                        ? `₹${Number(selectedStaff.baseSalary).toFixed(2)} / ${selectedStaff.salaryType || "Monthly"}`
                        : "Not Set"}
                    </p>
                  </div>
                </div>

                {selectedStaff.status === "Suspended" && selectedStaff.suspendedReason && (
                  <div className="mt-4 bg-red-900/30 border border-red-500/50 rounded-lg p-4">
                    <p className="text-sm text-red-300">
                      <strong>Suspension Reason:</strong> {selectedStaff.suspendedReason}
                    </p>
                    {selectedStaff.suspendedAt && (
                      <p className="text-sm text-red-300 mt-2">
                        <strong>Suspended At:</strong>{" "}
                        {new Date(selectedStaff.suspendedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* KYC Documents */}
              <div className="bg-slate-700/50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4 border-b border-slate-600 pb-2">
                  KYC Documents
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Aadhaar Card */}
                  <div>
                    <p className="text-sm text-gray-400 mb-2">Aadhaar Card</p>
                    {selectedStaff.aadharDocumentUrl ? (
                      <div className="space-y-2">
                        <div className="bg-slate-600 rounded-lg p-4 border border-slate-500">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">🆔</span>
                              <span className="text-white">
                                {selectedStaffAadhaarMeta.mode === "dual-image"
                                  ? "Aadhaar Front + Back"
                                  : "Aadhaar Card"}
                              </span>
                            </div>
                            <span className="text-green-400 text-sm">✓ Uploaded</span>
                          </div>
                        </div>
                        {selectedStaffAadhaarMeta.mode === "dual-image" ? (
                          <>
                            <a
                              href={selectedStaffAadhaarMeta.frontUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block w-full bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-center"
                            >
                              View Aadhaar Front
                            </a>
                            <a
                              href={selectedStaffAadhaarMeta.backUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block w-full bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-center"
                            >
                              View Aadhaar Back
                            </a>
                          </>
                        ) : (
                          <>
                            <a
                              href={selectedStaff.aadharDocumentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block w-full bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-center"
                            >
                              View Document
                            </a>
                            <a
                              href={selectedStaff.aadharDocumentUrl}
                              download
                              className="block w-full bg-slate-600 hover:bg-slate-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-center"
                            >
                              Download Document
                            </a>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="bg-slate-600 rounded-lg p-4 border border-slate-500 border-dashed">
                        <p className="text-gray-400 text-center">No Aadhaar card uploaded</p>
                      </div>
                    )}
                  </div>

                  {/* PAN Card */}
                  <div>
                    <p className="text-sm text-gray-400 mb-2">PAN Card</p>
                    {selectedStaff.panDocumentUrl ? (
                      <div className="space-y-2">
                        <div className="bg-slate-600 rounded-lg p-4 border border-slate-500">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">📄</span>
                              <span className="text-white">PAN Card</span>
                            </div>
                            <span className="text-green-400 text-sm">✓ Uploaded</span>
                          </div>
                        </div>
                        <a
                          href={selectedStaff.panDocumentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-center"
                        >
                          View Document
                        </a>
                        <a
                          href={selectedStaff.panDocumentUrl}
                          download
                          className="block w-full bg-slate-600 hover:bg-slate-500 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-center"
                        >
                          Download Document
                        </a>
                      </div>
                    ) : (
                      <div className="bg-slate-600 rounded-lg p-4 border border-slate-500 border-dashed">
                        <p className="text-gray-400 text-center">No PAN card uploaded</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Preview Section */}
              {(selectedStaff.aadharDocumentUrl || selectedStaff.panDocumentUrl) && (
                <div className="bg-slate-700/50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 border-b border-slate-600 pb-2">
                    Document Preview
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedStaff.aadharDocumentUrl && (
                      <div>
                        <p className="text-sm text-gray-400 mb-2">
                          {selectedStaffAadhaarMeta.mode === "dual-image"
                            ? "Aadhaar Front/Back Preview"
                            : "Aadhaar Card Preview"}
                        </p>
                        <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
                          {selectedStaffAadhaarMeta.mode === "dual-image" ? (
                            <div className="grid grid-cols-1 gap-3">
                              <img
                                src={selectedStaffAadhaarMeta.frontUrl}
                                alt="Aadhaar Front"
                                className="w-full h-auto rounded-lg max-h-64 object-contain"
                              />
                              <img
                                src={selectedStaffAadhaarMeta.backUrl}
                                alt="Aadhaar Back"
                                className="w-full h-auto rounded-lg max-h-64 object-contain"
                              />
                            </div>
                          ) : selectedStaff.aadharDocumentUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                            <img
                              src={selectedStaff.aadharDocumentUrl}
                              alt="Aadhaar Card"
                              className="w-full h-auto rounded-lg max-h-64 object-contain"
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "block";
                              }}
                            />
                          ) : null}
                          <div
                            style={{ display: "none" }}
                            className="text-center text-gray-400 py-8"
                          >
                            <p className="text-lg mb-2">📄</p>
                            <p>PDF Document - Click "View Document" to open</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedStaff.panDocumentUrl && (
                      <div>
                        <p className="text-sm text-gray-400 mb-2">PAN Card Preview</p>
                        <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
                          {selectedStaff.panDocumentUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                            <img
                              src={selectedStaff.panDocumentUrl}
                              alt="PAN Card"
                              className="w-full h-auto rounded-lg max-h-64 object-contain"
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "block";
                              }}
                            />
                          ) : null}
                          <div
                            style={{ display: "none" }}
                            className="text-center text-gray-400 py-8"
                          >
                            <p className="text-lg mb-2">📄</p>
                            <p>PDF Document - Click "View Document" to open</p>
                          </div>
                        </div>
                      </div>
                    )}
                    </div>
                </div>
            )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedStaff(null);
                }}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
                </div>
    );
}
